const PLAY_THRESHOLD = 0.25;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const saveData = navigator.connection?.saveData === true;
const initialized = new WeakSet();
const seen = new WeakSet();
const visible = new WeakSet();

const pageLoaded = new Promise((resolve) => {
  if (document.readyState === 'complete') resolve();
  else window.addEventListener('load', resolve, { once: true });
}).then(
  () =>
    new Promise((resolve) => {
      if ('requestIdleCallback' in window) requestIdleCallback(resolve, { timeout: 2000 });
      else setTimeout(resolve, 200);
    })
);

const pausedByToggle = (video) =>
  video.id &&
  document.querySelector(
    `video-playstate-toggle[data-playstate="paused"] [aria-controls~="${CSS.escape(video.id)}"]`
  );

const canAutoplay = (video) => !reducedMotion.matches && !saveData && !pausedByToggle(video);

const play = (video) => {
  if (!visible.has(video) || !video.paused || !canAutoplay(video)) return;
  video.muted = true;
  video.play()?.catch(() => {});
};

const posterObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach(({ isIntersecting, target }) => {
      if (!isIntersecting) return;
      posterObserver.unobserve(target);
      target.poster = target.dataset.poster;
    });
  },
  { rootMargin: '800px 0px' }
);

const playObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach(({ isIntersecting, intersectionRatio, target }) => {
      const firstCheck = !seen.has(target);
      seen.add(target);

      if (!isIntersecting || intersectionRatio < PLAY_THRESHOLD) {
        visible.delete(target);
        if (!target.paused) target.pause();
        return;
      }

      visible.add(target);
      if (firstCheck) pageLoaded.then(() => play(target));
      else play(target);
    });
  },
  { threshold: PLAY_THRESHOLD }
);

const init = (root = document) => {
  root.querySelectorAll('video[data-lazy-video]').forEach((video) => {
    if (initialized.has(video)) return;
    initialized.add(video);

    if (video.dataset.poster) posterObserver.observe(video);

    const posterImage = video.hasAttribute('data-poster-image') && video.previousElementSibling;
    if (posterImage instanceof HTMLImageElement) {
      const usePosterImage = () => {
        if (posterImage.currentSrc) video.poster = posterImage.currentSrc;
      };
      if (posterImage.complete) usePosterImage();
      else posterImage.addEventListener('load', usePosterImage, { once: true });
    }

    if (video.id && !canAutoplay(video)) {
      document
        .querySelector(`[aria-controls~="${CSS.escape(video.id)}"]`)
        ?.closest('video-playstate-toggle')
        ?.pause?.();
    }

    playObserver.observe(video);
  });
};

init();

new MutationObserver((mutations) => {
  mutations.forEach(({ addedNodes }) => {
    addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches('video[data-lazy-video]')) init(node.parentElement);
      else if (node.querySelector('video[data-lazy-video]')) init(node);
    });
  });
}).observe(document.documentElement, { childList: true, subtree: true });
